/**
 * Drop-in replacement for react-virtualized-auto-sizer (MIT).
 * Measures the parent element size and passes width/height to children.
 */
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

const AutoSizer = memo(
    ({
        children,
        defaultHeight = 0,
        defaultWidth = 0,
        disableHeight = false,
        disableWidth = false,
        doNotBailOutOnEmptyChildren = false,
        onResize,
        style = {},
        tagName: Tag = 'div',
        ...rest
    }) => {
        const autoSizerRef = useRef(null);
        const parentNodeRef = useRef(null);
        const resizeObserverRef = useRef(null);
        const timeoutIdRef = useRef(null);
        const dimensionsRef = useRef({ height: defaultHeight, width: defaultWidth });
        const onResizeRef = useRef(onResize);

        const [height, setHeight] = useState(defaultHeight);
        const [width, setWidth] = useState(defaultWidth);

        onResizeRef.current = onResize;

        const measureAndUpdate = useCallback(() => {
            timeoutIdRef.current = null;
            const parentNode = parentNodeRef.current;

            if (!parentNode) return;

            const computedStyle = window.getComputedStyle(parentNode) || {};
            const paddingLeft = parseFloat(computedStyle.paddingLeft || '0');
            const paddingRight = parseFloat(computedStyle.paddingRight || '0');
            const paddingTop = parseFloat(computedStyle.paddingTop || '0');
            const paddingBottom = parseFloat(computedStyle.paddingBottom || '0');
            const rect = parentNode.getBoundingClientRect();
            const nextHeight = rect.height - paddingTop - paddingBottom;
            const nextWidth = rect.width - paddingLeft - paddingRight;
            const { height: prevHeight, width: prevWidth } = dimensionsRef.current;
            const shouldUpdateHeight = !disableHeight && prevHeight !== nextHeight;
            const shouldUpdateWidth = !disableWidth && prevWidth !== nextWidth;

            if (!shouldUpdateHeight && !shouldUpdateWidth) return;

            if (shouldUpdateHeight) {
                dimensionsRef.current.height = nextHeight;
                setHeight(nextHeight);
            }

            if (shouldUpdateWidth) {
                dimensionsRef.current.width = nextWidth;
                setWidth(nextWidth);
            }

            onResizeRef.current?.({
                height: nextHeight,
                width: nextWidth,
                get scaledHeight() {
                    return nextHeight;
                },
                get scaledWidth() {
                    return nextWidth;
                },
            });
        }, [disableHeight, disableWidth]);

        useEffect(() => {
            const autoSizerNode = autoSizerRef.current;
            const parentNode = autoSizerNode?.parentNode;

            if (
                parentNode == null ||
                !parentNode.ownerDocument ||
                !parentNode.ownerDocument.defaultView ||
                !(parentNode instanceof parentNode.ownerDocument.defaultView.HTMLElement)
            ) {
                return undefined;
            }

            parentNodeRef.current = parentNode;

            const ResizeObserverInstance = parentNode.ownerDocument.defaultView.ResizeObserver;

            if (ResizeObserverInstance != null) {
                resizeObserverRef.current = new ResizeObserverInstance(() => {
                    timeoutIdRef.current = window.setTimeout(measureAndUpdate, 0);
                });
                resizeObserverRef.current.observe(parentNode);
            }

            measureAndUpdate();

            return () => {
                if (timeoutIdRef.current !== null) {
                    window.clearTimeout(timeoutIdRef.current);
                }
                resizeObserverRef.current?.disconnect();
                parentNodeRef.current = null;
            };
        }, [measureAndUpdate]);

        const outerStyle = { overflow: 'visible' };
        const childParams = {};
        let bailoutOnChildren = false;

        if (!disableHeight) {
            if (height === 0) bailoutOnChildren = true;
            outerStyle.height = 0;
            childParams.height = height;
            childParams.scaledHeight = height;
        }

        if (!disableWidth) {
            if (width === 0) bailoutOnChildren = true;
            outerStyle.width = 0;
            childParams.width = width;
            childParams.scaledWidth = width;
        }

        if (doNotBailOutOnEmptyChildren) {
            bailoutOnChildren = false;
        }

        return (
            <Tag
                ref={autoSizerRef}
                style={{
                    ...outerStyle,
                    ...style,
                }}
                {...rest}
            >
                {!bailoutOnChildren && children(childParams)}
            </Tag>
        );
    },
);

AutoSizer.displayName = 'AutoSizer';

AutoSizer.propTypes = {
    children: PropTypes.func.isRequired,
    defaultHeight: PropTypes.number,
    defaultWidth: PropTypes.number,
    disableHeight: PropTypes.bool,
    disableWidth: PropTypes.bool,
    doNotBailOutOnEmptyChildren: PropTypes.bool,
    onResize: PropTypes.func,
    style: PropTypes.shape({}),
    tagName: PropTypes.elementType,
};

export default AutoSizer;
